import { Box, Button, FormControl, FormErrorMessage, FormLabel, Grid, GridItem, Input, Select, CloseButton } from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import MyDiv from "./educationDetails.style";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CustomToast } from "../../../../../components";
import { useFetch } from "../../../../../hooks/useFetch";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";

interface EducationDetail {
    employeeId?: number
    education_Category_Id: number;
    education_Degree_Id: number;
    course: string;
    instituteName: string;
    grade: string;
    passingYear: string;
    file?: File | null;
    fileName?: string;
}

interface EducationDetailForm {
    educations: EducationDetail[];
}

interface CategoryDD {
    id: number;
    educationName: string;
}
interface DegreeDD {
    id: number;
    degreeName: string;
}

interface educationDetailsProps {
    nextStep: () => void;
    prevStep: () => void;
    educationDetail: any;
    setEducationDetail: React.Dispatch<React.SetStateAction<any>>;
    employeeId: number;
    setEmployeeId?: React.Dispatch<React.SetStateAction<number | null>>;
}

const schema = yup.object().shape({
    educations: yup.array().of(
        yup.object().shape({
            education_Category_Id: yup.number().required("Category is required").typeError("Category is required"),
            education_Degree_Id: yup.number().required("Degree is required").typeError("Degree is required"),
            course: yup.string().required("Course/Stream is required").matches(/^[A-Za-z\s]+$/, "Course/Stream can only contain letters and spaces"),
            instituteName: yup.string().required("Institute Name is required"),
            grade: yup
                .string()
                .required("Grade/Percentage/CGPA is required")
                .test(
                    "is-valid",
                    "Enter a valid Percentage (0–100) or CGPA (0–10)",
                    (value) => {
                        if (!value) return false;
                        const percentageRegex = /^(100(\.0{1,2})?|(\d{1,2})(\.\d{1,2})?)$/;
                        const cgpaRegex = /^(10(\.0{1,2})?|[0-9](\.\d{1,2})?)$/;
                        return percentageRegex.test(value) || cgpaRegex.test(value);
                    }
                ),
            passingYear: yup.string().required("Passing Year is required"),
        })
    ).required("At least one education detail is required").min(1, "At least one education detail is required")
});

export const EducationDetails = ({ nextStep, prevStep, educationDetail, setEducationDetail, employeeId }: educationDetailsProps) => {
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);
    const [loading, setLoading] = useState(false);
    const [educationCategory, setEductionCategory] = useState<CategoryDD[]>([]);
    const [educationDegrees, setEducationDegrees] = useState<{ [key: number]: DegreeDD[] }>({});

    const { handleSubmit, formState: { errors }, control, trigger, reset } = useForm<EducationDetailForm>({
        resolver: yupResolver(schema),
        defaultValues: educationDetail && educationDetail.educations?.length > 0
            ? { educations: educationDetail.educations }
            : { educations: [{ education_Category_Id: 0, education_Degree_Id: 0, course: "", instituteName: "", grade: "", passingYear: "", file: null, fileName: "" }] },
        mode: "onBlur"
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "educations"
    });



    useEffect(() => {
        if (educationDetail && educationDetail.educations && educationDetail.educations.length > 0) {
            reset({
                educations: educationDetail.educations,
            });

            educationDetail.educations.forEach((edu: any, idx: number) => {
                if (edu.education_Category_Id) {
                    Education_Degree(edu.education_Category_Id, idx);
                }
            });
        }
    }, [reset]);


    const onSubmit = async (data: EducationDetailForm) => {
        if (!employeeId) {
            addToast({ message: "Employee ID not found", status: "error" });
            return;
        }
        try {
            setLoading(true);
            const formDataPayload = new FormData(); 

            data.educations.forEach((edu, i) => {
                formDataPayload.append("EmployeeID", employeeId.toString());
                formDataPayload.append(`Educations[${i}].Education_Category_Id`, edu.education_Category_Id.toString());
                formDataPayload.append(`Educations[${i}].Education_Degree_Id`, edu.education_Degree_Id.toString());
                formDataPayload.append(`Educations[${i}].Course`, edu.course);
                formDataPayload.append(`Educations[${i}].InstituteName`, edu.instituteName);
                formDataPayload.append(`Educations[${i}].Grade`, edu.grade);
                formDataPayload.append(`Educations[${i}].PassingYear`, edu.passingYear);
                if (edu.file) {
                    formDataPayload.append(`Educations[${i}].File`, edu.file);
                }
            });
            setEducationDetail(data);
            const response = await fetchApi("Hrlense_Employee/NewEducationDeatails", "POST", formDataPayload, null, "Education Details posted successfully", true);
            if (response) {
                nextStep();
            }
        } catch (error) {
            addToast({ message: "Error uploading education details", status: "error" });
        } finally {
            setLoading(false);
        }
    };

    const Education_Cat = async () => {
        const response = await fetchApi("Hrlense_EducationCategories", "GET", null, "", "");
        if (response) setEductionCategory(response);
    };

    const Education_Degree = async (categoryId: number, index: number) => {
        const response = await fetchApi("dropdown/EduDegrees", "GET", null, `educationCategoryId=${categoryId}`, "");
        if (response) {
            setEducationDegrees((prev) => ({ ...prev, [index]: response }));
        }
    };

    useEffect(() => {
        Education_Cat();
    }, []);

    const handleAddEducation = async () => {
        const lastIndex = fields.length - 1;
        const isValid = await trigger(`educations.${lastIndex}`);
        if (!isValid) {
            addToast({ message: "Please fill in the current education before adding a new one.", status: "error" });
            return;
        }
        append({ education_Category_Id: 0, education_Degree_Id: 0, course: "", instituteName: "", grade: "", passingYear: "", file: null, fileName: "" });
    };

    return (
        <MyDiv>
            <Box m={6} as="form" onSubmit={handleSubmit(onSubmit)}>
                {fields.map((field, index) => (
                    <Box key={field.id} position="relative" border="1px solid #ddd" borderRadius="md" p={4} mb={6}>
                        {fields.length > 1 && (
                            <CloseButton size="sm" color="red.500" position="absolute" top={2} right={2} onClick={() => remove(index)} />
                        )}
                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                            <GridItem>
                                <FormControl mb={4} isInvalid={!!errors?.educations?.[index]?.education_Category_Id} isRequired>
                                    <FormLabel>Education Category</FormLabel>
                                    <Controller
                                        name={`educations.${index}.education_Category_Id`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select placeholder="Select Category" value={field.value ? field.value.toString() : ""} onChange={(e) => {
                                                field.onChange(Number(e.target.value));
                                                Education_Degree(Number(e.target.value), index);
                                            }}>
                                                {educationCategory.map((option) => (
                                                    <option key={option.id} value={option.id.toString()}>
                                                        {option.educationName}
                                                    </option>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {errors?.educations?.[index]?.education_Category_Id?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem>
                                <FormControl mb={4} isInvalid={!!errors?.educations?.[index]?.education_Degree_Id} isRequired>
                                    <FormLabel>Education Degree</FormLabel>
                                    <Controller
                                        name={`educations.${index}.education_Degree_Id`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select placeholder="Select Degree" value={field.value ? field.value.toString() : ""} onChange={(e) => field.onChange(Number(e.target.value))}>
                                                {(educationDegrees[index] || []).map((option) => (
                                                    <option key={option.id} value={option.id.toString()}>
                                                        {option.degreeName}
                                                    </option>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {errors?.educations?.[index]?.education_Degree_Id?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem>
                                <FormControl mb={4} isInvalid={!!errors?.educations?.[index]?.course} isRequired>
                                    <FormLabel>Course/Stream</FormLabel>
                                    <Controller
                                        name={`educations.${index}.course`}
                                        control={control}
                                        render={({ field }) => (
                                            <Input type="text" placeholder="Enter course/stream" value={field.value || ""} onChange={field.onChange} />
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {errors?.educations?.[index]?.course?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem>
                                <FormControl mb={4} isInvalid={!!errors?.educations?.[index]?.instituteName} isRequired>
                                    <FormLabel>Institute Name</FormLabel>
                                    <Controller
                                        name={`educations.${index}.instituteName`}
                                        control={control}
                                        render={({ field }) => (
                                            <Input type="text" placeholder="Enter institute name" value={field.value || ""} onChange={field.onChange} />
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {errors?.educations?.[index]?.instituteName?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem>
                                <FormControl mb={4} isInvalid={!!errors?.educations?.[index]?.grade} isRequired>
                                    <FormLabel>Percentage / CGPA</FormLabel>
                                    <Controller
                                        name={`educations.${index}.grade`}
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="number"
                                                step="0.01"
                                                max="100"
                                                placeholder="Enter Percentage / CGPA "
                                                value={field.value || ""}
                                                onChange={(e) => {
                                                    let val = e.target.value;
                                                    if (!/^\d*\.?\d{0,2}$/.test(val)) return;

                                                    let num = parseFloat(val);
                                                    if (num > 100) {
                                                        num = 100;
                                                    }

                                                    field.onChange(isNaN(num) ? "" : num.toString());
                                                }}
                                            />
                                        )}
                                    />

                                    <FormErrorMessage>
                                        {errors?.educations?.[index]?.grade?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem>
                                <FormControl mb={4} isInvalid={!!errors?.educations?.[index]?.passingYear} isRequired>
                                    <FormLabel>Passing Year</FormLabel>
                                    <Controller
                                        name={`educations.${index}.passingYear`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                placeholder="Select Passing Year"
                                                value={field.value ? field.value.split("-")[0] : ""}
                                                onChange={(e) => {
                                                    const year = e.target.value;
                                                    field.onChange(`${year}-01-01`);
                                                }}
                                            >
                                                {Array.from({ length: 50 }, (_, i) => {
                                                    const year = new Date().getFullYear() - i;
                                                    return (
                                                        <option key={year} value={year}>
                                                            {year}
                                                        </option>
                                                    );
                                                })}
                                            </Select>
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {errors?.educations?.[index]?.passingYear?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem colSpan={2}>
                                <FormControl mb={4}>
                                    <FormLabel>Upload Certificate</FormLabel>
                                    <Controller
                                        name={`educations.${index}.file`}
                                        control={control}
                                        render={({ field }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    accept="application/pdf,image/png,image/jpeg,image/jpg"
                                                    display="none"
                                                    id={`file-upload-${index}`}
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0] || null;
                                                        field.onChange(file);
                                                        if (file) {
                                                            setEducationDetail((prev: any) => {
                                                                const prevEducations = Array.isArray(prev?.educations) ? prev.educations : [];
                                                                const updated = [...prevEducations];
                                                                updated[index] = { ...updated[index], file, fileName: file.name };
                                                                return { ...prev, educations: updated };
                                                            });
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    as="label"
                                                    htmlFor={`file-upload-${index}`}
                                                    colorScheme="blue"
                                                    size="sm"
                                                    cursor="pointer"
                                                >
                                                    Choose File
                                                </Button>
                                                <Box as="span" ml={3} fontSize="md" color="gray.600">
                                                    {educationDetail?.educations?.[index]?.fileName || "No file chosen"}
                                                </Box>
                                            </>
                                        )}
                                    />
                                </FormControl>
                            </GridItem>

                        </Grid>
                    </Box>
                ))}
                <Button colorScheme="green" variant="outline" onClick={handleAddEducation}> + Add More </Button>
                <Box display="flex" justifyContent="space-between" mt={4}>
                    <Button onClick={prevStep} colorScheme="gray" leftIcon={<ArrowBackIcon boxSize={4} />}> Previous </Button>
                    <Button type="submit" isLoading={loading} colorScheme="green" rightIcon={<ArrowForwardIcon boxSize={4} />}> Save & Next </Button>
                </Box>
            </Box>
        </MyDiv>
    );
}; 
